import React, { useState } from "react";
import { useDispatch } from "react-redux";
import exportFromJSON from "export-from-json";
import { customReportExportToCsvUrlAction } from "../../redux/actions/custom-report-action";
import errorToast from "../helper-components/error-toast";

const extractCsvUrl = (data) => {
  if (!data) return null;
  if (typeof data === "string") return data;
  return (
    data.url ||
    data.csv_url ||
    data.file_url ||
    data.download_url ||
    (typeof data.data === "string" ? data.data : null) ||
    data.data?.url ||
    data.data?.csv_url ||
    data.data?.file_url ||
    data.data?.download_url ||
    null
  );
};
const MAX_RENAMEABLE_DOWNLOAD_BYTES = 20 * 1024 * 1024; // 20 MB

const clickDownloadLink = (href, filename) => {
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const downloadCsvFromUrl = async (csvUrl, filename) => {
  try {
    const headRes = await fetch(csvUrl, { method: "HEAD" });
    const size = Number(headRes.headers.get("content-length"));
    if (headRes.ok && size > 0 && size <= MAX_RENAMEABLE_DOWNLOAD_BYTES) {
      const res = await fetch(csvUrl);
      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      clickDownloadLink(objectUrl, filename);
      window.URL.revokeObjectURL(objectUrl);
      return;
    }
  } catch (err) {
    // HEAD blocked/failed (e.g. no CORS) - fall through to a direct download.
  }

  clickDownloadLink(csvUrl, filename);
};

const ExportDropdown = ({
  data = [],
  columns = [],
  reportName = "Custom Report",
  disabled = false,
  functionId = null,
  filterValues = null,
}) => {
  const dispatch = useDispatch();
  const [exporting, setExporting] = useState(false);

  const handleExportCsv = () => {
    if (functionId) {
      setExporting(true);
      dispatch(
        customReportExportToCsvUrlAction(
          {
            function_id: functionId,
            filter_values: filterValues || {},
          },
          async (response) => {
            const csvUrl = extractCsvUrl(response?.data ?? response);
            if (csvUrl) {
              await downloadCsvFromUrl(csvUrl, `${reportName}.csv`);
            } else {
              errorToast("Failed to get CSV download link");
            }
            setExporting(false);
          },
          (errorResponse) => {
            setExporting(false);
            errorToast(errorResponse?.data?.message || errorResponse?.message || "Failed to export CSV");
          }
        )
      );
      return;
    }

    if (data.length === 0) return;
    exportFromJSON({
      data,
      fileName: reportName,
      exportType: exportFromJSON.types.csv,
    });
  };

  const handleExportJson = () => {
    // JSON export only makes sense for the client-side (in-memory `data`)
    // path — the functionId path resolves to a server-generated CSV URL and
    // has no JSON equivalent to call into.
    if (functionId || data.length === 0) return;
    exportFromJSON({
      data,
      fileName: reportName,
      exportType: exportFromJSON.types.json,
    });
  };

  const isDisabled = exporting || disabled || (!functionId && data.length === 0);
  const isJsonDisabled = exporting || disabled || data.length === 0;

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={handleExportCsv}
        disabled={isDisabled}
        className={`flex items-center gap-2 px-4 py-2 text-xs fw_500 rounded-md border border-D0D5DD ${
          isDisabled
            ? "bg-F4F4F4 text-667085 cursor-not-allowed"
            : "bg-white text-344054 hover:bg-f3f8ff"
        }`}
      >
        {exporting ? "Exporting..." : "Export CSV"}
      </button>
      {!functionId && (
        <button
          onClick={handleExportJson}
          disabled={isJsonDisabled}
          className={`flex items-center gap-2 px-4 py-2 text-xs fw_500 rounded-md border border-D0D5DD ${
            isJsonDisabled
              ? "bg-F4F4F4 text-667085 cursor-not-allowed"
              : "bg-white text-344054 hover:bg-f3f8ff"
          }`}
        >
          Export JSON
        </button>
      )}
    </div>
  );
};

export default ExportDropdown;
